import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Building2, Calendar, Lock, ShieldAlert, ShieldCheck, ListChecks, Package } from 'lucide-react';
import '../shared/shared-chrome.css';
import { useRole } from '../../hooks/useRole';
import { useUrlState, boolUrlParser } from '../../hooks/useUrlState';
import { useListsContext } from '../../contexts/ListsContext';
import { useTimeFilterContext } from '../../contexts/TimeFilterContext';
import logger from '../../lib/logger';
import VendorPicker from './VendorPicker';
import AgencySelect from './AgencySelect';
import ProductFamilyChips from './ProductFamilyChips';
import SnapshotStrip from './SnapshotStrip';
import TrajectoryCharts from './TrajectoryCharts';
import PenetrationHeatmap from './PenetrationHeatmap';
import SecurityCoverageMatrix from './SecurityCoverageMatrix';
import CompetitiveMap from './CompetitiveMap';
import PartnerEcosystem from './PartnerEcosystem';
import PartnerCategorySpend from './PartnerCategorySpend';
import DisplacementTable from './DisplacementTable';
import RenewalCalendar from './RenewalCalendar';
import TakeoutView from './TakeoutView';
import PlanKanban from './PlanKanban';
import StrategicInitiatives from './StrategicInitiatives';
import SharedTransactionsDrilldownModal from '../shared/SharedTransactionsDrilldownModal';
import VendorTabs, { useVendorTabs } from './VendorTabs';
import { loggedGet, loggedMutation, apiClient } from './loggedFetch';
import './VendorCompetitiveAnalysis.css';
import './PenetrationHeatmap.css';
import './SecurityCoverageMatrix.css';
import './StrategicInitiatives.css';
import {
  fiscalYearOf,
  fiscalYearRange,
  formatFiscalYear,
  fiscalSpanLabel,
} from '@techstack/core';
import { useFiscalBasisContext } from '../../contexts/FiscalBasisContext';

function VendorCompetitiveAnalysis() {
  const { isSuperAdmin, roleLoading, isFeatureEnabled, role } = useRole();
  const hasAccess = isSuperAdmin || isFeatureEnabled('vendor_analysis');
  // Global time filter from context - selection persists across navigation
  const { years } = useTimeFilterContext();
  // Filter state lives in the URL so hard-refresh / bookmark / share
  // all restore the same view. Defaults are omitted from the URL —
  // an unfiltered visit stays at clean /vendor-analysis.
  const [vendor, setVendor] = useUrlState('vendor', null);

  // Default to Palo Alto Networks on first visit — for everyone, not just
  // palo_user (Ryan, 2026-08-24): the page should open showing analysis, the
  // way GAS does, never a pick-first wall. A ?vendor= URL still wins, and the
  // guard means switching vendors doesn't snap back.
  const [hasSetDefault, setHasSetDefault] = useState(false);
  useEffect(() => {
    if (!hasSetDefault && !roleLoading && !vendor) {
      setVendor('Palo Alto Networks');
      setHasSetDefault(true);
    }
  }, [hasSetDefault, roleLoading, vendor, setVendor]);
  const [includeLowConfidence, setIncludeLowConfidence] = useUrlState('lowConf', false, boolUrlParser);
  // Strict vendor evidence: only count rows whose item_description actually
  // mentions the canonical vendor or one of its aliases — same rule the
  // post-Phase-C trigger applies to new rows. Default ON because legacy
  // data has many pre-Phase-C mis-tags (e.g. Cohesity SmartFiles tagged as
  // Elastic with confidence 0.90). Toggle off to see raw classified data.
  const [strict, setStrict] = useUrlState('strict', true, boolUrlParser);
  // Phase F: sub-brand chip filter. null = "All". Resets when vendor changes
  // so picking PAN then switching to Cisco doesn't leave a stale CyberArk
  // chip applied.
  const [productFamily, setProductFamily] = useUrlState('productFamily', null);
  const [drilldown, setDrilldown] = useState(null);
  const [activeTab, setActiveTab] = useVendorTabs();
  const queryClient = useQueryClient();

  // Phase 3 (H.29): the active account list. When the user opts into list
  // scope (and a list is actually selected with matched departments), the
  // heatmap restricts to those departments + their SCPRS rollups. Persisted
  // in the URL like the other VCA filters so the scoped view is shareable.
  const { selectedList, selectedDepartmentIds, selectedMembers } = useListsContext();
  const [searchParams] = useSearchParams();
  const [listScoped, setListScoped] = useUrlState('listScoped', false, boolUrlParser);
  const hasList = !!selectedList && (selectedDepartmentIds?.length || 0) > 0;
  const scopedDeptIds = (listScoped && hasList) ? selectedDepartmentIds : null;

  // Phase H.29: Single-department focus mode. When user clicks a department
  // chip, we filter all tabs to that single department. null = show all list depts.
  // 'statewide' = explicitly show all 82 agencies (overrides list scoping).
  const [focusDeptId, setFocusDeptId] = useState(null); // number | 'statewide' | null

  // KPI cards visibility toggle — allows user to hide snapshot strip to see more graph
  const [snapshotCollapsed, setSnapshotCollapsed] = useState(false);

  // GAS-style account scope (Ryan, 2026-08-24): a first-class agency filter
  // that needs no account list. URL-persisted like every other VCA filter, so
  // /vendor-analysis?account=39 is shareable. Takes precedence over list
  // scoping and focus chips — one unambiguous winner beats a merge nobody can
  // predict from the UI.
  const [accountId, setAccountId] = useUrlState('account', null, {
    parse: (s) => (Number.isInteger(Number(s)) ? Number(s) : null),
    serialize: String,
  });

  // Effective department IDs: account > focus chip > list scope; statewide=null
  const isStatewide = focusDeptId === 'statewide' && !accountId;
  const effectiveDeptIds = accountId
    ? [accountId]
    : isStatewide
      ? null // statewide = no filtering
      : focusDeptId && focusDeptId !== 'statewide'
        ? [focusDeptId]
        : scopedDeptIds;
  // Scope label for component verbiage (e.g., "CDT" or "T5 - GovOps" or "statewide")
  const scopeLabel = isStatewide
    ? 'statewide'
    : focusDeptId
      ? selectedMembers?.find(m => m.department_id === focusDeptId)?.acronym || `Dept ${focusDeptId}`
      : scopedDeptIds
        ? selectedList?.name
        : 'statewide';

  // Default to scoped=true when user has a list and hasn't explicitly set listScoped
  const hasInitializedScopeRef = useRef(false);
  useEffect(() => {
    // Only run once when list data loads and no explicit URL param exists
    if (!hasInitializedScopeRef.current && hasList && !searchParams.has('listScoped')) {
      hasInitializedScopeRef.current = true;
      setListScoped(true);
      logger.vendorAnalysis.info('Heatmap:auto-scope-to-list', {
        list: selectedList?.name, departments: selectedDepartmentIds?.length,
      });
    }
  }, [hasList, searchParams, setListScoped, selectedList, selectedDepartmentIds]);

  useEffect(() => { setProductFamily(null); }, [vendor, setProductFamily]);

  useEffect(() => {
    if (scopedDeptIds) {
      logger.vendorAnalysis.info('Heatmap:list-scope-on', {
        list: selectedList?.name, departments: scopedDeptIds.length,
      });
    }
  }, [scopedDeptIds, selectedList]);

  // Reset focus when list changes or scoping is disabled
  useEffect(() => {
    setFocusDeptId(null);
  }, [selectedList?.id, listScoped]);

  // Log focus changes
  useEffect(() => {
    if (focusDeptId !== null) {
      const member = selectedMembers?.find(m => m.department_id === focusDeptId);
      logger.vendorAnalysis.info('VCA:focus-department', {
        department_id: focusDeptId,
        acronym: member?.acronym,
        list: selectedList?.name,
      });
    }
  }, [focusDeptId, selectedMembers, selectedList]);

  useEffect(() => {
    logger.vendorAnalysis.info('Page:mounted', { hasAccess, roleLoading });
    return () => logger.vendorAnalysis.info('Page:unmounted');
  }, [hasAccess, roleLoading]);

  useEffect(() => {
    if (vendor) {
      logger.vendorAnalysis.info('Page:vendor-changed', {
        vendor, include_low_confidence: includeLowConfidence, strict, tab: activeTab,
      });
    }
  }, [vendor, includeLowConfidence, strict, activeTab]);

  // Label fiscal-year drilldowns on the VIEWER's calendar.
  const { calendar: fiscalCalendar } = useFiscalBasisContext();

  // Build a context label for the drill-down header from the drilldown shape.
  const drilldownContext = useMemo(() => {
    if (!drilldown) return null;
    const parts = [];
    if (drilldown.department) parts.push(drilldown.department);
    if (drilldown.startDate && drilldown.endDate) {
      const sy = new Date(drilldown.startDate).getFullYear();
      const ey = new Date(drilldown.endDate).getFullYear();
      // A full fiscal year on the ACTIVE calendar gets the "FY27 (Aug 2026 –
      // Jul 2027)" treatment. This used to pattern-match -07-01/-06-30, so a
      // PANW-basis drilldown fell through to a bare "2026–2027".
      const fy = fiscalYearOf(drilldown.startDate, fiscalCalendar);
      const span = fiscalYearRange(fy, fiscalCalendar);
      if (drilldown.startDate === span.start && drilldown.endDate === span.end) {
        const [startMon, endMon] = fiscalSpanLabel(fiscalCalendar).split('–');
        parts.push(
          `${formatFiscalYear(fy, fiscalCalendar)} (${startMon} ${sy} – ${endMon} ${ey})`
        );
      } else {
        parts.push(`${sy}–${ey}`);
      }
    }
    return parts.length > 0 ? `· ${parts.join(' · ')}` : null;
  }, [drilldown, fiscalCalendar]);

  useEffect(() => {
    if (drilldown) {
      logger.vendorAnalysis.info('Drilldown:opening', {
        vendor: drilldown.value || drilldown.nodeValue,
        department: drilldown.department,
        department_id: drilldown.departmentId,
        start_date: drilldown.startDate,
        end_date: drilldown.endDate,
        strict,
        include_low_confidence: includeLowConfidence,
      });
    }
  }, [drilldown, strict, includeLowConfidence]);

  // Cross-tab "seed a plan card" mutation. Other tabs (heatmap, partners,
  // takeout, etc.) use this to drop a new card into the kanban backlog
  // without making the user navigate to the Plan tab first. Once seeded
  // we flip to the Plan tab so the user sees where the card landed.
  const seedPlan = useMutation({
    mutationFn: loggedMutation(
      'VendorCompetitiveAnalysis:seed-plan',
      ({ payload }) => apiClient.post(
        `/api/vendor-analysis/${encodeURIComponent(vendor)}/plan/cards`,
        payload
      )
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-analysis', 'plan', vendor] });
      setActiveTab('plan');
    },
  });

  const handleSeedPlan = useCallback((seed) => {
    if (!vendor || !seed) return;
    const body = {
      column_key: 'backlog',
      title: seed.title,
      rationale: seed.rationale || null,
      dollar_impact: seed.dollar_impact || null,
      due_date: seed.due_date || null,
      seed_kind: seed.kind || 'manual',
      seed_payload: seed,
    };
    seedPlan.mutate({ payload: body });
  }, [vendor, seedPlan]);

  if (roleLoading) {
    return <div className="vca-page vca-loading">Checking access…</div>;
  }

  if (!hasAccess) {
    return (
      <div className="vca-page vca-denied">
        <Lock size={32} />
        <h2>Restricted</h2>
        <p>This page requires Vendor Analysis access.</p>
      </div>
    );
  }

  return (
    <div className="vca-page">
      <header className="vca-header">
        <div>
          <h1 className="ts-page-title">Vendor Competitive Analysis</h1>
          <p className="ts-page-blurb">Footprint, trajectory, and 30-60-90 planning per vendor.</p>
        </div>
        <div className="vca-header-toggles">
          <label className="vca-toggle">
            <input
              type="checkbox"
              checked={strict}
              onChange={(e) => setStrict(e.target.checked)}
            />
            <span title="Only count rows whose description actually mentions the vendor or an alias (filters legacy mis-tags).">
              Strict vendor evidence
            </span>
          </label>
          <label className="vca-toggle">
            <input
              type="checkbox"
              checked={includeLowConfidence}
              onChange={(e) => setIncludeLowConfidence(e.target.checked)}
            />
            <span>Include low-confidence rows</span>
          </label>
          {hasList && (
            <label className="vca-toggle vca-toggle--list">
              <input
                type="checkbox"
                checked={listScoped}
                onChange={(e) => setListScoped(e.target.checked)}
              />
              <span title={`Scope the heatmap to "${selectedList.name}" (${selectedDepartmentIds.length} matched departments) plus their SCPRS rollups.`}>
                <ListChecks size={13} style={{ verticalAlign: 'text-bottom' }} />
                {' '}Scope to “{selectedList.name}”
              </span>
            </label>
          )}
        </div>
      </header>

      <div className="vca-filter-row">
        <VendorPicker value={vendor} onChange={setVendor} />
        <AgencySelect
          value={accountId}
          onChange={(id) => {
            setAccountId(id);
            // The account filter and the list-focus chips write to the same
            // scope; clear the chip so the UI shows one active winner.
            setFocusDeptId(null);
          }}
        />
      </div>

      {/* Phase H.29: Department chips for list scoping. Shows clickable chips per
          account so users can focus all tabs on a single department. Includes
          Statewide chip to explicitly view all 82 agencies. */}
      {vendor && hasList && selectedMembers?.length > 0 && (
        <div className="vca-dept-chips">
          <span className="vca-dept-chips-label">
            <ListChecks size={13} /> {selectedList?.name}:
          </span>
          <button
            type="button"
            className={`vca-dept-chip ${!accountId && !isStatewide && focusDeptId === null && listScoped ? 'is-active' : ''}`}
            onClick={() => { setListScoped(true); setFocusDeptId(null); }}
            title={`Show all ${selectedMembers.length} accounts in the list`}
          >
            All {selectedMembers.length}
          </button>
          {selectedMembers.map(member => (
            <button
              key={member.department_id}
              type="button"
              className={`vca-dept-chip ${focusDeptId === member.department_id ? 'is-active' : ''}`}
              onClick={() => {
                setListScoped(true);
                setFocusDeptId(focusDeptId === member.department_id ? null : member.department_id);
              }}
              title={member.official_name || member.common_name || member.acronym}
            >
              {member.acronym || member.official_name?.slice(0, 12) || member.common_name?.slice(0, 12) || `#${member.department_id}`}
            </button>
          ))}
          <button
            type="button"
            className={`vca-dept-chip vca-dept-chip--statewide ${!accountId && (isStatewide || (!listScoped && focusDeptId === null)) ? 'is-active' : ''}`}
            onClick={() => { setFocusDeptId('statewide'); }}
            title="Show all 82 California state agencies"
          >
            Statewide
          </button>
        </div>
      )}

      {vendor ? (
        <>
          <ProductFamilyChips
            vendor={vendor}
            value={productFamily}
            onChange={setProductFamily}
            strict={strict}
            includeLowConfidence={includeLowConfidence}
          />
          <VendorTabs activeKey={activeTab} onChange={setActiveTab} />

          {activeTab === 'trajectory' && (
            <>
              <SnapshotStrip
                vendor={vendor}
                includeLowConfidence={includeLowConfidence}
                strict={strict}
                productFamily={productFamily}
                departmentIds={effectiveDeptIds}
                listName={selectedList?.name}
                years={years}
                onDrilldown={setDrilldown}
                collapsed={snapshotCollapsed}
                onToggle={() => setSnapshotCollapsed(c => !c)}
              />
              <TrajectoryCharts
                vendor={vendor}
                includeLowConfidence={includeLowConfidence}
                strict={strict}
                productFamily={productFamily}
                departmentIds={effectiveDeptIds}
                listName={selectedList?.name}
                years={years}
                onDrilldown={setDrilldown}
              />
              <PenetrationHeatmap
                vendor={vendor}
                includeLowConfidence={includeLowConfidence}
                strict={strict}
                productFamily={productFamily}
                departmentIds={effectiveDeptIds}
                listName={selectedList?.name}
                years={years}
                onDrilldown={setDrilldown}
                onSeedPlan={handleSeedPlan}
              />
            </>
          )}

          {activeTab === 'security' && (
            <SecurityCoverageMatrix
              departmentIds={effectiveDeptIds}
              vendor={vendor}
              onDrilldown={setDrilldown}
            />
          )}

          {activeTab === 'competitive' && (
            <>
              <CompetitiveMap
                vendor={vendor}
                includeLowConfidence={includeLowConfidence}
                strict={strict}
                productFamily={productFamily}
                departmentIds={effectiveDeptIds}
                listName={selectedList?.name}
                years={years}
                onDrilldown={setDrilldown}
              />
              <DisplacementTable
                vendor={vendor}
                includeLowConfidence={includeLowConfidence}
                strict={strict}
                productFamily={productFamily}
                departmentIds={effectiveDeptIds}
                listName={selectedList?.name}
                years={years}
                onDrilldown={setDrilldown}
              />
            </>
          )}

          {activeTab === 'renewals' && (
            <RenewalCalendar
              vendor={vendor}
              includeLowConfidence={includeLowConfidence}
              strict={strict}
              productFamily={productFamily}
              departmentIds={effectiveDeptIds}
              listName={selectedList?.name}
              years={years}
              onDrilldown={setDrilldown}
            />
          )}

          {activeTab === 'takeout' && (
            <TakeoutView
              vendor={vendor}
              includeLowConfidence={includeLowConfidence}
              strict={strict}
              productFamily={productFamily}
              departmentIds={effectiveDeptIds}
              listName={selectedList?.name}
              years={years}
              onSeedPlan={handleSeedPlan}
            />
          )}

          {activeTab === 'partners' && (
            <>
              <PartnerCategorySpend
                vendor={vendor}
                departmentIds={effectiveDeptIds}
                onDrilldown={setDrilldown}
              />
              <PartnerEcosystem
                vendor={vendor}
                includeLowConfidence={includeLowConfidence}
                strict={strict}
                productFamily={productFamily}
                departmentIds={effectiveDeptIds}
                listName={selectedList?.name}
                years={years}
                onSeedPlan={handleSeedPlan}
              />
            </>
          )}

          {activeTab === 'plan' && (
            <PlanKanban
              vendor={vendor}
              includeLowConfidence={includeLowConfidence}
              strict={strict}
            />
          )}

          {activeTab === 'strategic' && (
            <StrategicInitiatives vendor={vendor} />
          )}
        </>
      ) : (
        <div className="vca-empty">
          <ShieldAlert size={28} />
          <p>Pick a vendor above to load the analysis.</p>
        </div>
      )}

      {drilldown && (
        <VcaDrilldown
          drilldown={drilldown}
          vendor={vendor}
          drilldownContext={drilldownContext}
          strict={strict}
          includeLowConfidence={includeLowConfidence}
          productFamily={productFamily}
          onClose={() => {
            logger.vendorAnalysis.info('Drilldown:closed');
            setDrilldown(null);
          }}
        />
      )}
    </div>
  );
}

// Thin wrapper that adapts the VCA-specific drilldown shape into the
// SharedTransactionsDrilldownModal's prop surface. Uses the shared
// TransactionTable from @techstack/ui with TekStak's render slots.
function VcaDrilldown({ drilldown, vendor, drilldownContext, strict, includeLowConfidence, productFamily, onClose }) {
  const vendorName = drilldown.value || drilldown.nodeValue || vendor;

  const fmtDate = (d) => (d
    ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    : '—');

  const path = useMemo(() => {
    const qs = new URLSearchParams();
    if (strict === false) qs.set('strict', 'false');
    if (includeLowConfidence) qs.set('includeLowConfidence', 'true');
    if (productFamily) qs.set('product_family', productFamily);
    if (drilldown.department) qs.set('department', drilldown.department);
    if (drilldown.component) qs.set('component', drilldown.component);
    if (drilldown.supplier) qs.set('supplier', drilldown.supplier);
    if (drilldown.securityCategory) qs.set('securityCategory', drilldown.securityCategory);
    if (drilldown.startDate) qs.set('startDate', drilldown.startDate);
    if (drilldown.endDate) qs.set('endDate', drilldown.endDate);
    qs.set('limit', '200');
    return `/api/vendor-analysis/${encodeURIComponent(vendorName || '')}/transactions?${qs}`;
  }, [vendorName, drilldown.department, drilldown.component, drilldown.supplier, drilldown.securityCategory, drilldown.startDate, drilldown.endDate, strict, includeLowConfidence, productFamily]);

  const chips = [
    drilldown.department && { icon: <Building2 size={12} />, label: drilldown.department },
    drilldown.component && { icon: <Package size={12} />, label: drilldown.component },
    drilldown.supplier && { icon: <Building2 size={12} />, label: drilldown.supplier },
    drilldown.securityCategory && { icon: <ShieldCheck size={12} />, label: drilldown.securityCategory, variant: 'filter' },
    (drilldown.startDate || drilldown.endDate) && {
      icon: <Calendar size={12} />,
      label: `${fmtDate(drilldown.startDate)} – ${fmtDate(drilldown.endDate)}`,
    },
    strict !== false && {
      icon: <ShieldCheck size={12} />,
      label: 'Strict vendor evidence',
      variant: 'filter',
    },
  ].filter(Boolean);

  const handleFlag = useCallback(async (row, currentlyFlagged) => {
    logger.vendorAnalysis.info('Drilldown:flag', {
      id: row.id, was_flagged: currentlyFlagged, vendor: vendorName,
    });
    // Use the classification_flags pipeline so flagged rows feed
    // classificationActiveLearning.getCandidates() and bubble to the
    // top of the AI re-review queue (flag_pressure weight = 8.0).
    if (currentlyFlagged) {
      await apiClient.post(`/api/classification/flags/item/${row.id}/resolve`, {
        resolution_type: 'dismissed_not_actionable',
        resolution_notes: `Unflagged from VCA drilldown (vendor=${vendorName})`,
        status: 'dismissed',
      });
    } else {
      await apiClient.post('/api/classification/flags', {
        item_id: row.id,
        flag_reason: `Flagged from Vendor Competitive Analysis drilldown — vendor=${vendorName}, component=${row.component_name || 'unknown'}`,
        flag_category: 'other',
        flag_severity: 'warning',
      });
    }
  }, [vendorName]);

  return (
    <SharedTransactionsDrilldownModal
      isOpen={true}
      onClose={onClose}
      title={`${vendorName} transactions`}
      contextLabel={drilldownContext}
      chips={chips}
      queryKey={[
        'vendor-analysis', 'transactions', vendorName,
        drilldown.department, drilldown.startDate, drilldown.endDate,
        strict, includeLowConfidence, productFamily,
      ]}
      queryFn={loggedGet('VendorTransactionsModal:fetch', path)}
      onFlagForReview={handleFlag}
      vendor={vendorName}
      departmentId={drilldown.departmentId}
      component={drilldown.component}
      productFamily={productFamily}
      startDate={drilldown.startDate}
      endDate={drilldown.endDate}
      defaultConfidenceMode={strict ? 'confirmed' : (includeLowConfidence ? 'all' : 'probable')}
      copyContext={{
        vendor: vendorName,
        department: drilldown.department || null,
        period: drilldownContext || null,
        filters: `confidence ≥ 75%, strict vendor evidence ${strict ? 'ON' : 'OFF'}`,
      }}
      footerNote="Totals reconcile with the page chart. Click a PO to open the full document; flag any misclassified rows for AI review."
    />
  );
}

export default VendorCompetitiveAnalysis;
